import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ContentType,
  type CitationResponse,
  type MediaAssetResponse,
  type MediaTagResponse,
  type SourceResponse
} from 'we-tree-api-client/v2';

import { useDomainStore, useMediaStore } from '@/hooks/useStores';
import { mediaApi } from '@/api/mediaWrapper';
import { citationsApi, sourcesApi } from '@/api/sourcesWrapper';
import MediaCard from '@/components/media/MediaCard';
import FamilyPageHeader from '@/components/layout/FamilyPageHeader';
import MediaUploadForm from '@/components/media/MediaUploadForm';
import MediaLibraryFiltersBar from '@/components/media/MediaLibraryFiltersBar';
import TagFilterDropdown from '@/components/shared/TagFilterDropdown';
import ImageEnlargeOverlay from '@/components/shared/ImageEnlargeOverlay';
import EditFamilyNameModal from '@/components/family/EditFamilyNameModal';
import Spinner from '@/components/shared/Spinner';
import ListResultsBar from '@/components/shared/ListResultsBar';
import ManageContentPermissionsModal from '@/components/modals/ManageContentPermissionsModal';
import { canDeleteMedia, canExportMedia, canModifyFamily } from '@/utils/rolePermissions';
import { canDeleteItem, canModifyItem } from '@/types/access';
import { familyHasPaidPlan } from '@/utils/familyPlan';
import { mimeTypeFilterParam } from '@/utils/mediaUpload';
import type { MediaTypeFilter } from '@/types/media-upload';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import ScreenTourLayer from '@/components/help/ScreenTourLayer';
import { useScreenTour } from '@/hooks/useScreenTour';
import Plus from '@/icons/Plus';
import ExportMediaModal from '@/components/media/ExportMediaModal';
import {
  FAMILY_MEDIA_TOUR_STEPS,
  FAMILY_MEDIA_TOUR_STORAGE_KEY,
  resolveFamilyMediaTourSteps
} from '@/data/family-media-tour';
import { DEFAULT_MEDIA_LIBRARY_FILTERS, type MediaLibraryFilters } from '@/types/media-library';
import {
  buildMediaAssetSourceMap,
  collectMediaYears,
  filterMediaAssets,
  hasActiveMediaLibraryFilters
} from '@/utils/mediaLibrary';

import './family-media-screen.css';

const FamilyMediaScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const domainStore = useDomainStore();
  const mediaStore = useMediaStore();
  const navigate = useNavigate();
  const { familyId: familyIdParam } = useParams<{ familyId: string }>();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isServerError, setIsServerError] = useState(false);
  const [items, setItems] = useState<MediaAssetResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [mediaTypeFilter, setMediaTypeFilter] = useState<MediaTypeFilter>('all');
  const [tags, setTags] = useState<MediaTagResponse[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [tagMatchAnd, setTagMatchAnd] = useState(false);
  const [libraryFilters, setLibraryFilters] = useState<MediaLibraryFilters>(DEFAULT_MEDIA_LIBRARY_FILTERS);
  const [sources, setSources] = useState<SourceResponse[]>([]);
  const [citations, setCitations] = useState<CitationResponse[]>([]);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [viewerAssetId, setViewerAssetId] = useState<string | null>(null);
  const [permissionsAsset, setPermissionsAsset] = useState<MediaAssetResponse | null>(null);

  const hasPaidPlan = familyHasPaidPlan(domainStore.currentFamily?.plan);

  const loadMedia = useCallback(async () => {
    if (!familyIdParam) return;
    setIsLoading(true);
    setErrorMessage(null);
    setIsServerError(false);
    const result = await mediaApi.searchMediaAssets(familyIdParam, {
      mimeType: mimeTypeFilterParam(mediaTypeFilter),
      tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      tagMatch: tagMatchAnd ? 'and' : 'or',
      page: 1,
      pageSize: 100
    });
    setIsLoading(false);
    if (result.success && result.data != null) {
      setItems(Array.isArray(result.data.items) ? result.data.items : []);
      setTotal(typeof result.data.total === 'number' ? result.data.total : 0);
      setIsServerError(false);
    } else {
      const message = result.success === false && result.error ? result.error.getUserMessage() : t('media.loadFailed');
      setErrorMessage(message);
      setIsServerError(result.success === false && result.error != null && result.error.status >= 500);
      setItems([]);
      setTotal(0);
    }
  }, [familyIdParam, selectedTagIds, tagMatchAnd, mediaTypeFilter, t]);

  useEffect(() => {
    if (!familyIdParam) {
      setErrorMessage(t('media.noFamily'));
      return;
    }
    setErrorMessage(null);
    domainStore.setSelectedFamily(familyIdParam);
  }, [domainStore, familyIdParam]);

  useEffect(() => {
    if (familyIdParam) {
      loadMedia();
    }
  }, [familyIdParam, loadMedia]);

  const loadTags = useCallback(async () => {
    if (!familyIdParam) return;
    const result = await mediaApi.listMediaTags(familyIdParam);
    if (result.success && result.data) setTags(result.data);
    else setTags([]);
  }, [familyIdParam]);

  useEffect(() => {
    loadTags();
  }, [familyIdParam, loadTags]);

  const loadSourceContext = useCallback(async () => {
    if (!familyIdParam) return;
    const [sourcesResult, citationsResult] = await Promise.all([
      sourcesApi.listSources(familyIdParam, { page: 1, pageSize: 100 }),
      citationsApi.listCitations(familyIdParam, { page: 1, pageSize: 200 })
    ]);
    setSources(sourcesResult.success && sourcesResult.data ? sourcesResult.data.items : []);
    setCitations(citationsResult.success && citationsResult.data ? citationsResult.data.items : []);
  }, [familyIdParam]);

  useEffect(() => {
    void loadSourceContext();
  }, [loadSourceContext]);

  const mediaSourceMap = useMemo(() => buildMediaAssetSourceMap(citations), [citations]);

  const availableYears = useMemo(() => collectMediaYears(items), [items]);

  const filteredItems = useMemo(
    () => filterMediaAssets(items, libraryFilters, mediaSourceMap),
    [items, libraryFilters, mediaSourceMap]
  );

  const filterSources = useMemo(
    () =>
      sources.map((source) => ({ id: source.id, title: source.title })).sort((a, b) => a.title.localeCompare(b.title)),
    [sources]
  );

  const viewerMimeType = useMemo(
    () =>
      filteredItems.find((a) => a.id === viewerAssetId)?.mimeType ??
      items.find((a) => a.id === viewerAssetId)?.mimeType,
    [filteredItems, items, viewerAssetId]
  );

  const handleTagsChange = useCallback(async () => {
    await loadMedia();
    await loadTags();
  }, [loadMedia, loadTags]);

  const handleUploadSuccess = useCallback(async () => {
    setShowUpload(false);
    await loadMedia();
    await loadTags();
  }, [loadMedia, loadTags]);

  const handleDeleteMedia = useCallback(
    async (mediaAssetId: string) => {
      if (!familyIdParam) return;
      const result = await mediaStore.deleteMediaAsset(familyIdParam, mediaAssetId);
      if (result.success) {
        if (viewerAssetId === mediaAssetId) setViewerAssetId(null);
        setErrorMessage(null);
        await loadMedia();
        return;
      }
      setErrorMessage(mediaStore.lastError || 'Failed to delete media.');
    },
    [familyIdParam, loadMedia, mediaStore, viewerAssetId]
  );

  const handleEditNameSuccess = (displayName: string, alternativeName: string | null) => {
    if (!familyIdParam || !domainStore.currentFamily) return;
    domainStore.updateFamilyMetadata({
      id: familyIdParam,
      name: domainStore.currentFamily.name,
      displayName,
      alternativeName,
      memberCount: domainStore.currentFamily.memberCount,
      createdAt: domainStore.currentFamily.createdAt,
      updatedAt: domainStore.currentFamily.updatedAt,
      createdBy: domainStore.currentFamily.createdBy ?? null,
      createdByNickname: domainStore.currentFamily.createdByNickname ?? null,
      updatedBy: domainStore.currentFamily.updatedBy ?? null,
      updatedByNickname: domainStore.currentFamily.updatedByNickname ?? null,
      userRole: domainStore.currentFamily.userRole ?? null,
      plan: domainStore.currentFamily.plan
    });
  };

  // Family-role fallbacks only. Per-asset access from the API wins where it is available, because a
  // caller can hold edit rights on an asset (e.g. their own profile photo) that their family role
  // alone would not grant.
  const canEditMedia = canModifyFamily(domainStore.currentFamily?.userRole);
  const canDeleteMediaAssets = canDeleteMedia(domainStore.currentFamily?.userRole);
  const canExportLibrary = canExportMedia(domainStore.currentFamily?.userRole);
  const familyDisplayName = domainStore.currentFamily?.displayName || domainStore.currentFamily?.name || 'Family';

  const mediaTour = useScreenTour({
    steps: FAMILY_MEDIA_TOUR_STEPS,
    completedStorageKey: FAMILY_MEDIA_TOUR_STORAGE_KEY,
    resolveSteps: (steps) => resolveFamilyMediaTourSteps(steps, items.length > 0)
  });

  if (errorMessage && !familyIdParam) {
    return (
      <div className="media-page">
        <div className="media-page__error" role="alert">
          <h3 className="media-page__error-title">{errorMessage}</h3>
          <button type="button" className="btn btn--ghost" onClick={() => navigate('/families')}>
            Back to Families
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="media-page">
      <FamilyPageHeader
        familyDisplayName={familyDisplayName}
        sectionTitle={t('media.title')}
        onEditFamilyName={() => setShowEditNameModal(true)}
        onStartTour={mediaTour.startTour}
        introTourId="media-intro"
        intro={
          <>
            {t('media.lede')} <ScreenHelpDot />
          </>
        }
        introTrailing={
          canEditMedia || canExportLibrary ? (
            <div className="media-page__header-actions">
              {canExportLibrary ? (
                <button
                  type="button"
                  className="btn btn--ghost media-page__export-btn"
                  onClick={() => setShowExportModal(true)}
                  title={t('media.exportLibraryTitle')}
                >
                  {tForms('actions.exportLibrary')}
                </button>
              ) : null}
              {canEditMedia ? (
                <button
                  type="button"
                  className={`btn ${showUpload ? 'btn--ghost' : 'btn--primary'} media-page__add-btn`}
                  onClick={() => setShowUpload((s) => !s)}
                  aria-expanded={showUpload}
                >
                  {!showUpload && <Plus width={16} height={16} />}
                  {showUpload ? tCommon('cancel') : tForms('actions.uploadMedia')}
                </button>
              ) : null}
            </div>
          ) : undefined
        }
      />

      {canEditMedia && showUpload && (
        <div className="media-page__upload-panel">
          <MediaUploadForm
            familyId={familyIdParam!}
            acceptKinds="all"
            hasPaidPlan={hasPaidPlan}
            onSuccess={() => {
              void handleUploadSuccess();
            }}
          />
        </div>
      )}

      <div className="media-page__filter-bar" data-tour="media-filters">
        <div className="media-page__filter-bar__start">
          <div className="list-seg" role="tablist" aria-label={t('media.filterMediaType')}>
            <button
              type="button"
              role="tab"
              aria-selected={mediaTypeFilter === 'all'}
              className={`list-seg__btn${mediaTypeFilter === 'all' ? ' list-seg__btn--active' : ''}`}
              onClick={() => setMediaTypeFilter('all')}
            >
              {t('filters.all')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mediaTypeFilter === 'images'}
              className={`list-seg__btn${mediaTypeFilter === 'images' ? ' list-seg__btn--active' : ''}`}
              onClick={() => setMediaTypeFilter('images')}
            >
              {tForms('mediaPicker.images')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mediaTypeFilter === 'video'}
              className={`list-seg__btn${mediaTypeFilter === 'video' ? ' list-seg__btn--active' : ''}`}
              onClick={() => setMediaTypeFilter('video')}
            >
              {tForms('mediaPicker.video')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mediaTypeFilter === 'audio'}
              className={`list-seg__btn${mediaTypeFilter === 'audio' ? ' list-seg__btn--active' : ''}`}
              onClick={() => setMediaTypeFilter('audio')}
            >
              {tForms('mediaPicker.audio')}
            </button>
          </div>
          <MediaLibraryFiltersBar
            filters={libraryFilters}
            onChange={setLibraryFilters}
            years={availableYears}
            sources={filterSources}
          />
        </div>
        <div className="media-page__tag-filter">
          <TagFilterDropdown
            id="family-media-tag-filter"
            tags={tags}
            selectedTagIds={selectedTagIds}
            tagMatchAnd={tagMatchAnd}
            onSelectedTagsChange={setSelectedTagIds}
            onTagMatchChange={setTagMatchAnd}
            ariaLabel={t('media.filterByTag')}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="media-page__loading">
          <Spinner message={t('media.loading')} />
        </div>
      ) : errorMessage ? (
        <div className="media-page__error-inline" role="alert" data-server-error={isServerError ? 'true' : undefined}>
          <p>{errorMessage}</p>
          <button type="button" className="btn btn--ghost btn--sm" onClick={loadMedia}>
            {tCommon('retry')}
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="media-page__empty">
          <p className="media-page__empty-title">{t('media.emptyTitle')}</p>
          <p className="media-page__empty-body">
            {canEditMedia ? t('media.emptyBodyCanEdit') : t('media.emptyBodyReadOnly')}
          </p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="media-page__empty">
          <p className="media-page__empty-title">{t('media.noMatchTitle')}</p>
          <p className="media-page__empty-body">
            {hasActiveMediaLibraryFilters(libraryFilters) || selectedTagIds.length > 0
              ? t('media.noMatchFilters')
              : t('media.noMatchGeneric')}
          </p>
        </div>
      ) : (
        <>
          <ListResultsBar
            countLabel={
              <>
                {filteredItems.length !== items.length
                  ? t('media.resultsOf', { filtered: filteredItems.length, total: items.length })
                  : t('media.resultsCount', { filtered: filteredItems.length })}
                {total > items.length ? t('media.resultsLibraryTotal', { total }) : ''}
              </>
            }
          />
          <div className="media-page__grid" data-tour="media-grid">
            {filteredItems.map((asset) => (
              <div key={asset.id} className="media-page__grid-item">
                <MediaCard
                  familyId={familyIdParam!}
                  asset={asset}
                  layout="masonry"
                  availableTags={tags}
                  onTagsChange={handleTagsChange}
                  canEdit={canModifyItem(asset, canEditMedia)}
                  canDelete={canDeleteItem(asset, canDeleteMediaAssets)}
                  onDelete={(id) => {
                    void handleDeleteMedia(id);
                  }}
                  isDeleting={mediaStore.deletingMediaAssetId === asset.id}
                  onSelect={(id) => setViewerAssetId(id)}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {familyIdParam && domainStore.currentFamily && (
        <EditFamilyNameModal
          familyId={familyIdParam}
          currentDisplayName={domainStore.currentFamily.displayName || domainStore.currentFamily.name}
          canonicalName={domainStore.currentFamily.name}
          hasAlternative={!!domainStore.currentFamily.alternativeName}
          isOpen={showEditNameModal}
          onClose={() => setShowEditNameModal(false)}
          onSuccess={handleEditNameSuccess}
        />
      )}

      {familyIdParam && viewerAssetId && (
        <ImageEnlargeOverlay
          isOpen={Boolean(viewerAssetId)}
          onClose={() => setViewerAssetId(null)}
          familyId={familyIdParam}
          mediaAssetId={viewerAssetId}
          mimeType={viewerMimeType}
          onManagePermissions={(asset) => {
            setViewerAssetId(null);
            setPermissionsAsset(asset);
          }}
        />
      )}

      {familyIdParam && permissionsAsset && (
        <ManageContentPermissionsModal
          familyId={familyIdParam}
          contentType={ContentType.MediaAsset}
          contentId={permissionsAsset.id}
          contentLabel={permissionsAsset.originalFilename?.trim() || 'this media item'}
          canManage={permissionsAsset.canManageAccess === true}
          actorEffectiveRole={permissionsAsset.effectiveRole ?? null}
          isOpen={Boolean(permissionsAsset)}
          onClose={() => setPermissionsAsset(null)}
        />
      )}

      {familyIdParam && canExportLibrary ? (
        <ExportMediaModal familyId={familyIdParam} isOpen={showExportModal} onClose={() => setShowExportModal(false)} />
      ) : null}

      <ScreenTourLayer tour={mediaTour} />
    </div>
  );
});

export default FamilyMediaScreen;
